import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {useSaveProductionGuardView} from '../../../hooks/productions/useProductions';
import {
  SaveProductionGuardViewPayload,
  ProductionGuardQuery,
} from '../../../api/types/production.type';
import ArrButton from '../../../components/common/arr-button/ArrButton';
import ArrToast from '../../../components/common/arr-toast/ArrToast';
import {TOAST_DURATION} from '../../../shared/constants';

const SaveProductionGuardViewApiTest: React.FC = () => {
  // Request parameters
  const [productionId, setProductionId] = useState<string | number>(59);
  const [securityGroupId, setSecurityGroupId] = useState<string | number>(1);
  const [totalDocumentsCount, setTotalDocumentsCount] = useState<
    string | number
  >('5');
  const [familyDocumentsCount, setFamilyDocumentsCount] = useState<
    string | number
  >('0');
  const [isMarkComplete, setIsMarkComplete] = useState<boolean>(false);
  const [phPrivilegeTagList, setPhPrivilegeTagList] = useState<string>('');
  const [privDocPlaceHolder, setPrivDocPlaceHolder] = useState<string>('');
  const [enablePrivDocPlaceHolder, setEnablePrivDocPlaceHolder] =
    useState<boolean>(false);
  const [moduleType, setModuleType] = useState<string>('PRODUCTION');

  // Query parameters
  const [sequenceNumber, setSequenceNumber] = useState<string | number>('1');
  const [criteriaForUI, setCriteriaForUI] = useState<string>(
    'tags: [ name: ["Attorney_Client"] ]'
  );
  const [criteriaForDB, setCriteriaForDB] = useState<string>(
    'tags: [ name: ["Attorney_Client"] ]'
  );
  const [operatorCode, setOperatorCode] = useState<string>('');

  // Response state
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastStatus, setToastStatus] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('success');
  const [toastMessage, setToastMessage] = useState<string>('');

  const {
    data: saveResponse,
    mutate: saveProductionGuardView,
    error: saveError,
    isPending,
  } = useSaveProductionGuardView(
    (data) => {
      setShowToast(true);
      setToastStatus('success');
      setToastMessage(data.message || 'Information Saved Successfully');
      console.log('Save successful:', data);
    },
    (error) => {
      setShowToast(true);
      setToastStatus('error');
      setToastMessage(error.message || 'Failed to save production guard view');
      console.error('Save failed:', error);
    }
  );

  const handleSave = () => {
    const queries: ProductionGuardQuery[] = [
      {
        SequenceNumber: sequenceNumber,
        CriteriaForUI: criteriaForUI,
        CriteriaForDB: criteriaForDB,
        OperatorCode: operatorCode,
      },
    ];

    const payload: SaveProductionGuardViewPayload = {
      TotalDocumentsCount: totalDocumentsCount,
      FamilyDocumentsCount: familyDocumentsCount,
      ProductionId: productionId,
      IsMarkComplete: isMarkComplete,
      PHPrivilegeTagList: phPrivilegeTagList,
      SecurityGroupId: securityGroupId,
      lstProductionGuardQueries: queries,
      PrivDocPlaceHolder: privDocPlaceHolder,
      EnablePrivDocPlaceHolder: enablePrivDocPlaceHolder,
      ModuleType: moduleType,
    };

    saveProductionGuardView(payload);
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h5"
        className="typo-text headline3_size"
        gutterBottom
      >
        Save Production Guard View
      </Typography>
      <Box sx={{display: 'grid', gap: 2, maxWidth: 800, marginTop: 2}}>
        {/* Basic Information */}
        <Typography variant="h6" className="typo-text subhead2_size">
          Basic Information
        </Typography>

        <TextField
          label="Production Id"
          type="text"
          value={productionId}
          onChange={(e) => setProductionId(e.target.value)}
          size="small"
        />

        <TextField
          label="Security Group Id"
          type="text"
          value={securityGroupId}
          onChange={(e) => setSecurityGroupId(e.target.value)}
          size="small"
        />

        <TextField
          label="Total Documents Count"
          type="text"
          value={totalDocumentsCount}
          onChange={(e) => setTotalDocumentsCount(e.target.value)}
          size="small"
        />

        <TextField
          label="Family Documents Count"
          type="text"
          value={familyDocumentsCount}
          onChange={(e) => setFamilyDocumentsCount(e.target.value)}
          size="small"
        />

        <TextField
          label="Module Type"
          type="text"
          value={moduleType}
          onChange={(e) => setModuleType(e.target.value)}
          size="small"
        />

        <FormControlLabel
        data-testid="is-mark-complete-form-control-label-save-production-gaurd"
          control={
            <Switch
              checked={isMarkComplete}
              onChange={(e) => setIsMarkComplete(e.target.checked)}
            />
          }
          label="Is Mark Complete"
        />

        {/* Privilege Document Settings */}
        <Typography
          variant="h6"
          className="typo-text subhead2_size"
          sx={{marginTop: 2}}
        >
          Privilege Document Settings
        </Typography>

        <TextField
          label="PH Privilege Tag List"
          type="text"
          value={phPrivilegeTagList}
          onChange={(e) => setPhPrivilegeTagList(e.target.value)}
          size="small"
          multiline
          rows={2}
        />

        <TextField
          label="Priv Doc Place Holder"
          type="text"
          value={privDocPlaceHolder}
          onChange={(e) => setPrivDocPlaceHolder(e.target.value)}
          size="small"
          multiline
          rows={2}
        />

        <FormControlLabel
        data-testid="enable-priv-doc-place-holder-control-label-save-production-gaurd"
          control={
            <Switch
              checked={enablePrivDocPlaceHolder}
              onChange={(e) => setEnablePrivDocPlaceHolder(e.target.checked)}
            />
          }
          label="Enable Priv Doc Place Holder"
        />

        {/* Production Guard Queries */}
        <Typography
          variant="h6"
          className="typo-text subhead2_size"
          sx={{marginTop: 2}}
        >
          Production Guard Queries
        </Typography>

        <TextField
          label="Sequence Number"
          type="text"
          value={sequenceNumber}
          onChange={(e) => setSequenceNumber(e.target.value)}
          size="small"
        />

        <TextField
          label="Criteria For UI"
          type="text"
          value={criteriaForUI}
          onChange={(e) => setCriteriaForUI(e.target.value)}
          size="small"
          multiline
          rows={2}
        />

        <TextField
          label="Criteria For DB"
          type="text"
          value={criteriaForDB}
          onChange={(e) => setCriteriaForDB(e.target.value)}
          size="small"
          multiline
          rows={2}
        />

        <TextField
          label="Operator Code"
          type="text"
          value={operatorCode}
          onChange={(e) => setOperatorCode(e.target.value)}
          size="small"
        />

        {/* Action Buttons */}
        <Box sx={{display: 'flex', gap: 1, marginTop: 2}}>
          <ArrButton
            onClick={handleSave}
            disabled={isPending}
            dataTestId="save-production-guard-view-api-test-button">
            {isPending ? 'Saving...' : 'Save Production Guard View'}
          </ArrButton>
        </Box>

        {/* Response Display */}
        {saveResponse && (
          <Box
            sx={{
              backgroundColor: '#f0f8ff',
              padding: 2,
              borderRadius: 1,
              marginTop: 2,
            }}
          >
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              gutterBottom
            >
              Response:
            </Typography>
            <Box
              component="pre"
              sx={{
                backgroundColor: '#fff',
                padding: 2,
                borderRadius: 1,
                overflow: 'auto',
                fontSize: '12px',
              }}
            >
              {JSON.stringify(saveResponse, null, 2)}
            </Box>
          </Box>
        )}

        {saveError && (
          <Box
            sx={{
              backgroundColor: '#fff3e0',
              padding: 2,
              borderRadius: 1,
              marginTop: 2,
            }}
          >
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              color="error"
              gutterBottom
            >
              Error:
            </Typography>
            <Typography className="typo-text body3_size" color="error">
              {(saveError as Error).message}
            </Typography>
          </Box>
        )}
      </Box>
      {/* Toast Notification */}
      {showToast && (
        <ArrToast
          open={showToast}
          message={toastMessage}
          severity={toastStatus}
          onClose={() => setShowToast(false)}
          duration={TOAST_DURATION}
        />
      )}
    </Box>
  );
};

export default SaveProductionGuardViewApiTest;
